import { BedDouble, CookingPot, Sofa, Tv, Wifi, Bath, Check, Hospital, Coffee, Utensils, Signpost, Car } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { photoSrc } from "@/lib/api";

const SPACE_ICONS = [BedDouble, CookingPot, Sofa, Tv, Wifi, Bath];
const LOC_ICONS = [Hospital, Coffee, Utensils, Signpost, Car];

const Photo = ({ p, className, testid }) =>
  p ? <img src={photoSrc(p.url)} alt={p.caption || "Studio photo"} loading="lazy" className={`w-full h-full object-cover ${className || ""}`} data-testid={testid} /> : <div className={`bg-secondary w-full h-full ${className || ""}`} />;

export const Welcome = () => {
  const { site } = useSite();
  const photos = site?.photos || [];
  return (
    <section className="section-pad bg-white border-y border-border" data-testid="welcome-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 aspect-square rounded-lg overflow-hidden border border-border"><Photo p={photos[1] || photos[0]} testid="welcome-photo" /></div>
        <div className="lg:col-span-6 lg:col-start-7">
          <p className="eyebrow">A personal welcome</p>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium">Your own space, with your host nearby</h2>
          <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground" data-testid="welcome-text">
            {site?.content.welcome.split("\n").filter(Boolean).map((t, i) => <p key={i}>{t}</p>)}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Space = () => {
  const { site } = useSite();
  const c = site?.content;
  const photos = site?.photos || [];
  return (
    <section id="space" className="section-pad scroll-mt-16" data-testid="space-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">The Space · 400 sq ft</p>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium">Everything You Need for a Comfortable Stay</h2>
          </div>
          <p className="lg:col-span-5 text-muted-foreground leading-relaxed">{c?.space_intro}</p>
        </div>
        <div className="mt-14 grid lg:grid-cols-12 gap-10">
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-x-10">
            {c?.space_items.map((it, i) => {
              const Icon = SPACE_ICONS[i] || Check;
              return (
                <li key={i} className="py-6 border-t border-border flex gap-4" data-testid={`space-item-${i}`}>
                  <Icon className="w-5 h-5 mt-1 text-[hsl(var(--sage))] shrink-0" strokeWidth={1.5} />
                  <div><p className="font-serif text-2xl">{it.title}</p><p className="text-sm text-muted-foreground mt-1">{it.text}</p></div>
                </li>
              );
            })}
          </ul>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="aspect-[3/4] rounded-lg overflow-hidden border border-border"><Photo p={photos[3]} /></div>
            <div className="aspect-[3/4] rounded-lg overflow-hidden border border-border mt-10"><Photo p={photos[4]} /></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const Amenities = () => {
  const { site } = useSite();
  return (
    <section id="amenities" className="section-pad bg-[hsl(var(--cream))] scroll-mt-16" data-testid="amenities-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <p className="eyebrow">Amenities</p>
          <h2 className="mt-4 text-3xl sm:text-4xl font-medium">What's included</h2>
        </div>
        <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10">
          {site?.content.amenities.map((a, i) => (
            <li key={i} className="flex items-center gap-3 py-4 border-b border-border" data-testid={`amenity-${i}`}>
              <Check className="w-4 h-4 text-[hsl(var(--sage))]" /> {a}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export const Location = () => {
  const { site } = useSite();
  const c = site?.content;
  return (
    <section id="location" className="section-pad scroll-mt-16" data-testid="location-section">
      <div className="max-w-7xl mx-auto">
        <p className="eyebrow">Location</p>
        <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-medium max-w-3xl">Close to What Brings You to Beaumont</h2>
        <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">A quiet residential street near Calder Avenue and 11th Street, convenient for hospital visits, work trips, and short stays.</p>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-border">
          {c?.location_items.map((l, i) => {
            const Icon = LOC_ICONS[i] || Check;
            return (
              <div key={i} className="p-6 border-r border-b border-border bg-white" data-testid={`location-item-${i}`}>
                <Icon className="w-5 h-5 text-[hsl(var(--sage))]" strokeWidth={1.5} />
                <p className="font-serif text-xl mt-4">{l.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{l.detail}</p>
              </div>
            );
          })}
        </div>
        {c?.show_address && c.address && (
          <div className="mt-8" data-testid="location-map">
            <p className="text-sm mb-3">{c.address}</p>
            <iframe title="Map" className="w-full h-80 rounded-lg border border-border" loading="lazy" src={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}&output=embed`} />
          </div>
        )}
      </div>
    </section>
  );
};
